import React, {Component} from "react";
import {Button, Card, Input, Tag, Tooltip, Typography, Radio} from "antd";
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined,
    LoadingOutlined,
    SaveOutlined,
    TagOutlined,
    EyeOutlined,
    EditOutlined,
} from '@ant-design/icons';
import CodeMirrorEditor from './CodeMirrorEditor';
import { marked } from 'marked';
import {createNote, updateNote} from "../../util/APIUtils";

const MIN_FONT = 11;
const MAX_FONT = 24;
const DEFAULT_FONT = 14;
const AUTO_SAVE_DELAY = 5000;

class NoteCreationBox extends Component {
    constructor(props) {
        super(props);
        this.state = {
            title: props.note?.title || '',
            content: props.note?.content || '',
            tags: props.note?.tags || [],
            isEditing: false,
            newTag: '',
            saveStatus: 'saved',
            isDirty: false,
            fontSize: DEFAULT_FONT,
            mode: 'edit', // 'edit' | 'preview'
        };
        this._autoSaveTimer = null;
        this._savedNoteId = props.note?.id || null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.note !== this.props.note) {
            // Cancel any pending auto-save when switching notes
            if (this._autoSaveTimer) {
                clearTimeout(this._autoSaveTimer);
                this._autoSaveTimer = null;
            }
            this._savedNoteId = this.props.note?.id || null;
            this.setState({
                title: this.props.note?.title || '',
                content: this.props.note?.content || '',
                tags: this.props.note?.tags || [],
                saveStatus: 'saved',
                isDirty: false,
                mode: 'edit',
            });
        }
    }

    componentWillUnmount() {
        // Flush pending save on unmount
        if (this._autoSaveTimer) {
            clearTimeout(this._autoSaveTimer);
            this._autoSaveTimer = null;
        }
        if (this.state.isDirty) {
            this.executeSave();
        }
    }

    scheduleAutoSave = () => {
        if (this._autoSaveTimer) {
            clearTimeout(this._autoSaveTimer);
        }
        this.setState({ saveStatus: 'unsaved', isDirty: true });
        this._autoSaveTimer = setTimeout(() => {
            this.executeSave();
        }, AUTO_SAVE_DELAY);
    }

    executeSave = () => {
        const {title, content, tags} = this.state;
        const note = {
            title: title || content.substring(0, 44) || 'Untitled Note',
            content: content,
            tags: tags,
            folder: this.props.note?.folder || null
        };

        const isUpdate = this._savedNoteId && !String(this._savedNoteId).startsWith('temp-');
        const apiCall = isUpdate ? updateNote(this._savedNoteId, note) : createNote(note);

        this.setState({ saveStatus: 'saving' });

        apiCall
            .then((response) => {
                this._savedNoteId = response.id;
                this.setState({ saveStatus: 'saved', isDirty: false });
                if (this.props.onUpdate) {
                    this.props.onUpdate(response);
                }
            })
            .catch((error) => {
                console.error('Auto-save failed:', error);
                this.setState({ saveStatus: 'error' });
            });
    }

    handleSave = () => {
        if (this._autoSaveTimer) {
            clearTimeout(this._autoSaveTimer);
            this._autoSaveTimer = null;
        }
        this.executeSave();
    }

    handleTitleChange = (e) => {
        this.setState({title: e.target.value}, () => this.scheduleAutoSave());
    }

    handleContentChange = (value) => {
        this.setState({content: value}, () => this.scheduleAutoSave());
        if (this.props.onChange) {
            this.props.onChange(value);
        }
    }

    handleAddTag = () => {
        const {newTag, tags} = this.state;
        if (newTag.trim()) {
            const newTagNames = newTag.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
            const existingTagNames = tags.map(tag => tag.name);
            
            const tagsToAdd = newTagNames
                .filter(tagName => !existingTagNames.includes(tagName))
                .map(tagName => ({
                    name: tagName
                }));
            
            if (tagsToAdd.length > 0) {
                this.setState(
                    { tags: [...tags, ...tagsToAdd], newTag: '' },
                    () => this.scheduleAutoSave()
                );
            } else {
                this.setState({ newTag: '' });
            }
        }
    }

    handleRemoveTag = (tagToRemove) => {
        this.setState(
            { tags: this.state.tags.filter(tag => tag.name !== tagToRemove) },
            () => this.scheduleAutoSave()
        );
    }

    handleNewTagChange = (e) => {
        this.setState({newTag: e.target.value});
    }

    handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleAddTag();
        }
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleAddTag();
        }
    }

    handleTagInputChange = (e) => {
        const value = e.target.value;
        this.setState({ newTag: value }, () => {
            // Auto-add tags when comma is typed
            if (value.includes(',')) {
                this.handleAddTag();
            }
        });
    }

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }

    editorDidMount(editor) {
        console.log('editorDidMount', editor);
    }

    render() {
        const {title, content, tags, newTag, saveStatus, fontSize, mode} = this.state;

        const saveStatusEl = (() => {
            if (saveStatus === 'saving') return <span style={{fontSize:12,color:'#1890ff',display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}><LoadingOutlined spin /> Saving…</span>;
            if (saveStatus === 'saved') return <span style={{fontSize:12,color:'#52c41a',display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}><CheckCircleOutlined /> Saved</span>;
            if (saveStatus === 'unsaved') return <span style={{fontSize:12,color:'#faad14',display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}><ExclamationCircleOutlined /> Unsaved</span>;
            if (saveStatus === 'error') return <span style={{fontSize:12,color:'#ff4d4f',display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}><ExclamationCircleOutlined /> Save failed</span>;
        })();

        return (
            <div className="note-creation-box" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Card className="note-editor-card" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }} bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 0, overflow: 'hidden' }}>
                    {/* Title row */}
                    <div style={{ padding: '12px 16px 8px', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Input
                                value={title}
                                onChange={this.handleTitleChange}
                                placeholder="Untitled Note"
                                bordered={false}
                                style={{ fontSize: '18px', fontWeight: 600, color: '#1a1a1a', flex: 1, padding: '0', boxShadow: 'none' }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: 'auto' }}>
                                <Tooltip title="Title is auto-saved after 5 seconds of inactivity." placement="bottom">
                                    <QuestionCircleOutlined style={{ color: '#d9d9d9', fontSize: '14px', cursor: 'help' }} />
                                </Tooltip>
                                {saveStatusEl}
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    onClick={this.handleSave}
                                    loading={saveStatus === 'saving'}
                                    style={{ borderRadius: 6 }}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Toolbar: zoom + mode */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => this.setState(p => ({ fontSize: Math.max(MIN_FONT, p.fontSize - 1) }))} disabled={fontSize <= MIN_FONT} style={{ fontWeight: 'bold', fontSize: 16, lineHeight: 1, padding: '0 8px' }}>−</Button>
                        <span style={{ fontSize: 12, color: '#8c8c8c', minWidth: 36, textAlign: 'center' }}>{fontSize}px</span>
                        <Button size="small" onClick={() => this.setState(p => ({ fontSize: Math.min(MAX_FONT, p.fontSize + 1) }))} disabled={fontSize >= MAX_FONT} style={{ fontWeight: 'bold', fontSize: 16, lineHeight: 1, padding: '0 8px' }}>+</Button>
                        <Button size="small" onClick={() => this.setState({ fontSize: DEFAULT_FONT })} style={{ fontSize: 11, padding: '0 8px' }}>Reset</Button>

                        <div style={{ marginLeft: 'auto' }}>
                            <Radio.Group value={mode} onChange={e => this.setState({ mode: e.target.value })} size="small" buttonStyle="solid">
                                <Radio.Button value="edit"><EditOutlined /> Edit</Radio.Button>
                                <Radio.Button value="preview"><EyeOutlined /> Preview</Radio.Button>
                            </Radio.Group>
                        </div>
                    </div>

                    {/* Editor / Preview */}
                    <div className="note-editor-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {mode === 'edit' ? (
                            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <CodeMirrorEditor
                                    value={content}
                                    fontSize={fontSize}
                                    onChange={this.handleContentChange}
                                />
                            </div>
                        ) : (
                            <div
                                className="note-markdown-preview"
                                style={{
                                    flex: 1,
                                    padding: '20px 24px',
                                    overflow: 'auto',
                                    lineHeight: 1.8,
                                    fontSize: fontSize,
                                    minHeight: 0,
                                }}
                                dangerouslySetInnerHTML={{ __html: marked(content || '_Nothing to preview_') }}
                            />
                        )}
                    </div>

                    <div className="note-tags-section" style={{ flexShrink: 0 }}>
                        <div className="note-tags-input">
                            <Input
                                value={newTag}
                                onChange={this.handleTagInputChange}
                                onKeyDown={this.handleKeyDown}
                                placeholder="Add tags (separated by comma)..."
                                prefix={<TagOutlined />}
                                onPressEnter={this.handleAddTag}
                                className="tag-input"
                                style={{ fontSize: '16px' }}
                            />
                            <Button
                                type="dashed"
                                onMouseDown={(e) => { e.preventDefault(); this.handleAddTag(); }}
                                onClick={this.handleAddTag}
                                disabled={!newTag.trim()}
                            >
                                Add
                            </Button>
                        </div>

                        {tags.length > 0 && (
                            <div className="note-tags-display">
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {tags.map((tag, index) => (
                                        <Tag
                                            key={tag.id || index}
                                            closable
                                            onClose={() => this.handleRemoveTag(tag.name)}
                                            color="blue"
                                        >
                                            {tag.name}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        )
    }
}

export default NoteCreationBox;